import type { EpisodeListFilters } from "@/api/datasetEpisode";
import { missingChannelEpisodeFilters } from "@/components/dataset/episodeReviewState";
import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { GnbMenu } from "@/components/base";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import {
  DatasetDetailActions,
  DatasetDetailBody,
  datasetDetailTitleBadge,
  datasetDetailTabs,
  datasetDetailModeTab,
  defaultDatasetDetailTab,
  normalizeDatasetDetailTab,
  type DatasetDetailTab,
} from "./DatasetDetailContent";
import { DatasetDetailModals } from "./DatasetDetailModals";
import { formatDatasetPageTitle } from "./datasetDetailFormatters";
import {
  useDatasetDetailController,
  type DatasetDetailController,
} from "./useDatasetDetailController";

const defaultDatasetDetailPageTitle = "Dataset";

export function DatasetDetailPage() {
  const params = useParams<{ datasetUuid: string }>();
  const datasetUuid = params.datasetUuid ?? "";
  // 탭을 전환해도 리마운트되지 않는 상위가 데이터셋 적재·액션 상태를 소유한다. 제목·헤더 액션·
  // 본문이 같은 controller 를 공유한다. 그래서 탭 이동으로 본문이 다시 마운트되어도 제목이
  // 깜박이지 않는다.
  const [currentTab, setCurrentTab] = useState<DatasetDetailTab>(defaultDatasetDetailTab);
  // 에피소드 탭의 필터. 제목의 채널 누락 배지가 바꾼다. 다른 데이터셋으로 이동하면 비운다. 탭을 떠났다
  // 돌아와 FileBrowser 가 다시 마운트될 때 이전 데이터셋의 필터를 받지 않게 하기 위해서다. 마운트된
  // FileBrowser 는 데이터셋이 바뀌면 자기 필터를 스스로 비운다.
  const [episodeFilters, setEpisodeFilters] = useState<EpisodeListFilters | undefined>(undefined);
  // 가드가 보류한 탭 전환. 라우트 페이지에는 창 닫기가 없다. 그래서 이탈은 탭 전환 하나뿐이다.
  // 라우트 페이지에는 창 셸이 없다. 그래서 리뷰 액션 뒤 별도 후속 동작이 없다(상세 재조회는
  // controller 가 한다).
  const controller = useDatasetDetailController(datasetUuid, {
    reviewEnabled: true,
    annotationEnabled: currentTab === "annotation",
    onRequestTab: setCurrentTab,
  });

  useEffect(() => {
    // 다른 데이터셋으로 이동하면(예: Related 카드 클릭) 항상 리뷰 탭부터 보여준다. 제목은
    // controller.currentDataset 에서 파생된다. 그래서 별도 초기화가 필요 없다(적재 전에는 기본 제목).
    setCurrentTab(defaultDatasetDetailTab);
    setEpisodeFilters(undefined);
  }, [datasetUuid]);

  const openMissingChannelEpisodes = (): void => {
    setCurrentTab("episodeReview");
    setEpisodeFilters(missingChannelEpisodeFilters());
  };

  const pageTitle = controller.currentDataset
    ? formatDatasetPageTitle(controller.currentDataset)
    : defaultDatasetDetailPageTitle;

  // 에피소드/파일·리뷰 탭은 본문이 뷰포트를 채우고 내부만 스크롤한다. 그래서 페이지 스크롤을 끈다.
  // 상세 탭은 콘텐츠 길이에 따라 페이지가 스크롤된다.
  const scrollable = false;

  return (
    <>
      <DatasetDetailPageView
        pageTitle={pageTitle}
        titleAside={datasetDetailTitleBadge(controller, openMissingChannelEpisodes)}
        headerActions={<DatasetDetailActions controller={controller} />}
        currentMenu={GnbMenu.Dataset}
        showBackButton
        scrollable={scrollable}
        controller={controller}
        activeTab={currentTab}
        episodeFilters={episodeFilters}
        tabs={{
          items: datasetDetailTabs,
          value: datasetDetailModeTab(currentTab),
          defaultValue: defaultDatasetDetailTab,
          ariaLabel: "Dataset 상세 섹션",
          onValueChange: (value) => setCurrentTab(normalizeDatasetDetailTab(value)),
        }}
      />
      <DatasetDetailModals controller={controller} />
    </>
  );
}

type DatasetDetailPageViewProps = PageBaseProps & {
  activeTab: DatasetDetailTab;
  controller: DatasetDetailController;
  episodeFilters: EpisodeListFilters | undefined;
};

class DatasetDetailPageView extends PageBase<DatasetDetailPageViewProps> {
  protected override renderContent(_context: PageBaseContext): ReactNode {
    return (
      <DatasetDetailBody
        controller={this.props.controller}
        activeTab={this.props.activeTab}
        episodeFilters={this.props.episodeFilters}
      />
    );
  }
}
